export const WEFAT_CLIPBOARD_API_NAMES = Object.freeze(['setClipboardData']);
export class WxClipboardRuntimeError extends Error {
    api = 'setClipboardData';
    code;
    errCode;
    errMsg;
    remediation;
    constructor(code, reason, remediation, cause) {
        const errMsg = `setClipboardData:fail ${reason}`;
        super(`${code} ${errMsg}`);
        this.name = 'WxClipboardRuntimeError';
        this.code = code;
        this.errCode = code;
        this.errMsg = errMsg;
        this.remediation = remediation;
        if (cause !== undefined)
            Object.defineProperty(this, 'cause', { value: cause });
    }
}
const CALLBACK_KEYS = new Set(['success', 'fail', 'complete']);
const OPTION_KEYS = new Set([...CALLBACK_KEYS, 'data']);
function isPlainRecord(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function optionError(reason) {
    return new WxClipboardRuntimeError('WF6181', reason, 'Pass a plain options object with string data and callable success, fail, and complete callbacks.');
}
function environmentError(reason, cause) {
    if (cause instanceof WxClipboardRuntimeError)
        return cause;
    return new WxClipboardRuntimeError('WF6180', reason, 'Use a secure browser context, allow clipboard access, and call wx.setClipboardData from a user interaction.', cause);
}
function readOptions(raw) {
    if (!isPlainRecord(raw))
        throw optionError('options must be a plain object');
    for (const key of Reflect.ownKeys(raw)) {
        const descriptor = typeof key === 'string' ? Object.getOwnPropertyDescriptor(raw, key) : undefined;
        if (typeof key !== 'string' || !OPTION_KEYS.has(key)) {
            throw optionError(`unsupported option field ${String(key)}`);
        }
        if (descriptor === undefined || !('value' in descriptor)) {
            throw optionError(`option field ${key} must be a direct data property`);
        }
        if (CALLBACK_KEYS.has(key) && descriptor.value !== undefined && typeof descriptor.value !== 'function') {
            throw optionError(`${key} must be a function when provided`);
        }
    }
    if (typeof raw.data !== 'string')
        throw optionError('data must be a string');
    return raw;
}
function invokeCallback(callback, value, kind, report) {
    if (callback === undefined)
        return;
    try {
        callback(value);
    }
    catch (error) {
        if (report !== undefined) {
            try {
                report(error, 'setClipboardData', kind);
                return;
            }
            catch (reportError) {
                queueMicrotask(() => { throw reportError; });
                return;
            }
        }
        queueMicrotask(() => { throw error; });
    }
}
function browserEnvironment() {
    if (typeof navigator === 'undefined')
        return null;
    return {
        writeText(data) {
            let clipboard;
            try {
                clipboard = navigator.clipboard;
            }
            catch (error) {
                throw environmentError('browser clipboard access failed', error);
            }
            if (clipboard === undefined || typeof clipboard.writeText !== 'function') {
                throw environmentError('browser clipboard is unavailable');
            }
            return clipboard.writeText(data);
        },
    };
}
export function createBrowserClipboardWxApis(options = {}) {
    if (options.onCallbackError !== undefined && typeof options.onCallbackError !== 'function') {
        throw new TypeError('onCallbackError must be a function when provided');
    }
    if (options.onCopied !== undefined && typeof options.onCopied !== 'function') {
        throw new TypeError('onCopied must be a function when provided');
    }
    const environment = options.environment ?? browserEnvironment();
    if (environment === null || !isPlainRecord(environment) || typeof environment.writeText !== 'function') {
        throw new TypeError('createBrowserClipboardWxApis requires a browser navigator or injected environment');
    }
    return Object.freeze({
        setClipboardData(raw) {
            const callbacks = readOptions(raw);
            const task = Promise.resolve()
                .then(() => environment.writeText(callbacks.data))
                .then(() => options.onCopied?.(callbacks.data))
                .then(() => Object.freeze({ errMsg: 'setClipboardData:ok' }))
                .catch((error) => {
                throw environmentError(error instanceof Error ? error.message : 'browser clipboard write failed', error);
            });
            if (callbacks.success === undefined && callbacks.fail === undefined && callbacks.complete === undefined) {
                return task;
            }
            void task.then((result) => {
                invokeCallback(callbacks.success, result, 'success', options.onCallbackError);
                invokeCallback(callbacks.complete, result, 'complete', options.onCallbackError);
            }, (error) => {
                invokeCallback(callbacks.fail, error, 'fail', options.onCallbackError);
                invokeCallback(callbacks.complete, error, 'complete', options.onCallbackError);
            });
            return undefined;
        },
    });
}
//# sourceMappingURL=clipboard-adapter.js.map